import isEmpty from 'lodash/isEmpty';
import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import uuidV4 from 'uuid/v4';
import classNames from 'classnames';
import {window} from 'ssr-window';
import FormElementError from '@spothero/ui/form-element-error';
import Label from '@spothero/ui/label';
import TextButton from '@spothero/ui/text-button';
import TextInput from '@spothero/ui/text-input';
import CreditAPI from 'api/credit';

export default class PromocodeControl extends Component {
    static propTypes = {
        className: PropTypes.string,
        value: PropTypes.string,
        active: PropTypes.bool,
        onChange: PropTypes.func,
        verifyInline: PropTypes.bool,
        referralCode: PropTypes.bool,
        error: PropTypes.string,
        /** Placeholder text to put in the promo code input. If one is provided then no label is applied. */
        placeholder: PropTypes.string,
        spotId: PropTypes.number,
        rate: PropTypes.object,
        userEmail: PropTypes.string,
        showCancel: PropTypes.bool,
        autoFocus: PropTypes.bool,
        enterSubmits: PropTypes.bool,
        onPromoAdded: PropTypes.func,
        onCancelPromoCode: PropTypes.func
    };
    static defaultProps = {
        value: '',
        placeholder: '',
        showCancel: true,
        autoFocus: true
    };

    constructor(props) {
        super(props);

        const {
            value,
            active,
            error
        } = props;

        this.state = {
            value,
            active,
            error,
            isLoading: false,
            key: uuidV4()
        };
    }

    componentDidMount() {
        const {
            value
        } = this.state;

        if (!isEmpty(value)) {
            this.submit();
        }
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            onChange,
            onCancelPromoCode
        } = this.props;
        const {
            active,
            value
        } = this.state;

        if (onChange) {
            onChange(value);
        }

        if (prevState.active !== active && !active && onCancelPromoCode) {
            onCancelPromoCode();
        }
    }

    componentWillUnmount() {
        this._removeListeners();
    }

    _onToggleActiveClick = () => {
        this.setState((prevState, props) => {
            return {
                active: !prevState.active,
                isLoading: false,
                error: null,
                value: '',
                key: uuidV4()
            };
        });
    }

    _onInputFocus = evt => {
        window.addEventListener('keydown', this._onKeyDown);
    }

    _onInputBlur = evt => {
        this._removeListeners();
    }

    _onInputChange = evt => {
        this.setState({
            value: evt.target.value,
            error: null
        });
    }

    _onInvalid = validationState => {
        const {
            referralCode,
            showCancel
        } = this.props;
        const codeType = (referralCode) ? 'referral' : 'promo';

        this.setState({
            error: `Please enter a ${codeType} code${(showCancel) ? ' or click Cancel' : ''}.`
        });
    }

    _onKeyDown = evt => {
        if (this.props.enterSubmits && evt.which === 13) {
            this._onApplyClick();
        }
    }

    _onApplyClick = () => {
        this._checkCode();
    }

    _removeListeners = () => {
        window.removeEventListener('keydown', this._onKeyDown);
    }

    _checkCode = async evt => {
        const {
            spotId,
            rate,
            userEmail,
            onPromoAdded
        } = this.props;
        const {
            value
        } = this.state;

        if (!userEmail) {
            this.setState({
                error: 'You must first be logged in or provide an email address above.'
            });
        } else if (!isEmpty(value)) {
            this.setState({
                isLoading: true
            });

            try {
                const {
                    data: {
                        data
                    }
                } = await CreditAPI.checkPromoCode({
                    /* eslint-disable camelcase */
                    promo_code: value,
                    parking_spot_id: spotId,
                    rule_type: rate.rule_type,
                    price: rate.price,
                    email: userEmail,
                    rule_group_id: rate.rule_group_id
                    /* eslint-enable camelcase */
                });

                onPromoAdded(value, data);
            } catch (error) {
                if (error.data && error.data.data) {
                    error.data = error.data.data;
                }

                this.setState({
                    error: error.data.errors[0].messages[0],
                    isLoading: false
                });
            }
        }
    }

    submit() {
        const {
            active,
            value
        } = this.state;

        if (active) {
            return {promoCode: value};
        } else {
            return true;
        }
    }

    render() {
        const {
            className,
            verifyInline,
            referralCode,
            placeholder,
            showCancel,
            autoFocus
        } = this.props;
        const {
            value,
            active,
            isLoading,
            error,
            key
        } = this.state;
        const errorArray = (error) ? [error] : error;
        const classes = classNames(
            'PromocodeControl',
            'FormElement',
            {ContainsErrors: error},
            className
        );
        const label = (isEmpty(placeholder))
            ? `${(referralCode) ? 'Referral' : 'Promo'} Code`
            : null;
        const isApplyDisabled = (isEmpty(value) || isLoading);
        const codeType = (referralCode) ? 'referral' : 'promo';

        return (
            <div className={classes}>
                {(active)
                    ? (
                        <Fragment>
                            <TextInput
                                key={key}
                                className={(isEmpty(value)) ? '' : 'PromocodeControl-text-entered'}
                                name="promoCode"
                                label={<Label>{label}</Label>}
                                placeholder={placeholder}
                                defaultValue={value}
                                autoFocus={autoFocus}
                                onChange={this._onInputChange}
                                onFocus={this._onInputFocus}
                                onBlur={this._onInputBlur}
                                onInvalid={this._onInvalid}
                                required
                            />
                            <div className="PromocodeControl-controls">
                                {(verifyInline) &&
                                    <TextButton
                                        data-test="apply-promocode"
                                        disabled={isApplyDisabled}
                                        loading={isLoading}
                                        onClick={this._onApplyClick}
                                    >
                                        Apply
                                    </TextButton>
                                }
                                {showCancel &&
                                    <TextButton onClick={this._onToggleActiveClick}>Cancel</TextButton>
                                }
                            </div>
                        </Fragment>
                    ) : (
                        <TextButton
                            data-test="have-promocode"
                            onClick={this._onToggleActiveClick}
                        >
                            Add {codeType} code
                        </TextButton>
                    )
                }
                {(errorArray && errorArray.length > 0) &&
                    errorArray.map((e, i) => {
                        return (
                            <FormElementError key={i}>{e}</FormElementError>
                        );
                    })
                }
            </div>
        );
    }
}
